import { inject, injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';
import { IOsiOsvWidgetViewModel } from '@shared/types/mobx/widgets/OsiOsvWidget';
import { Abonent } from '@shared/types/osi/abonents';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import moment from 'moment';
import api from '@app/api';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { SettingsStore } from '@mobx/services/SettingsStore';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { queryClient } from '@shared/api/reactQuery';
import axios from 'axios';
import i18next from 'i18next';
import { LOCALE, LOCALES } from '@shared/utils/i18n/locales';
// OSI-808: свёртка строк ОСВ в итоги по категориям услуг + раскладка их в строки грида
import { getOsvCategoryTotals } from '@shared/utils/helpers/osvCategoryTotals';
import { buildOsvTotalRows, type OsvTotalRow } from '@shared/utils/helpers/osvTotalRows';
// OSI-808: messages/tokens — те же словари react-intl, что и у хука useTranslation в виджете
import { messages, tokens } from '@shared/utils/i18n';
import { makeFlat } from '@shared/utils/helpers/makeFlat';
import { sumBy } from 'lodash';
import { MIME, downloadBase64 } from '@shared/utils/download';
import { getGetApiTransactionsOsvOsiIdQueryOptions } from '@/shared/api/orval/transactions/transactions';
import { type ReportsOSVAbonent } from '@/shared/api/orval/models';
import { AreaTypeCodes } from '@shared/types/dictionaries';
// Утилита схлопывания дочерних помещений (паркинги/кладовки) в группы услуг родителя
import { buildChildToParent, collapseOsvChildren } from '@shared/utils/helpers/collapseChildAbonents';
// Загрузка абонентов ОСИ — нужна для построения карты child→parent
import { getOsiAbonents } from '@shared/api/osi/abonents/get';

@injectable()
export class OsiOsvWidgetViewModel implements IOsiOsvWidgetViewModel {
  isLoading = false;

  totalsOsv: any[] = [];

  // OSI-808: закреплённые снизу грида строки итогов — «ИТОГО» + по строке на каждую
  // категорию услуг дома. Заменяют старый футер с итогами: раз проп totalRows передан,
  // OsvGrid футер не рисует (в т.ч. до загрузки, чтобы он не мелькал). Поле observable (makeAutoObservable) и пересобирается ТОЛЬКО в
  // rebuildRows — т.е. ровно там же и из тех же строк, что видит пользователь.
  osvTotalRows: OsvTotalRow[] = [];

  dateFieldValue = new Date();

  // Флаг «Свернуть дочерние помещения» — по умолчанию ВКЛЮЧЁН.
  // По умолчанию дочерние помещения свёрнуты (паркинги/кладовки влиты в родителя):
  // class field инициализируется до тела конструктора, поэтому первичный loadOsv/rebuildRows
  // уже строит строки в свёрнутом виде, а чекбокс при открытии вкладки отмечен.
  isCollapseChildren = true;

  // Сырой список абонентов ОСВ из последнего ответа — кэш для пересборки строк без сетевого запроса
  private _rawOsvAbonents: ReportsOSVAbonent[] = [];

  // Карта child→parent (дочернее помещение → родитель) для схлопывания
  private _childToParent: Map<number, number> = new Map();

  private _abonents: Abonent[] = [];

  private _endDate = moment(this.dateFieldValue).endOf('month').toDate();

  private _startDate = moment(this.dateFieldValue).startOf('month').toDate();

  private _handleLangChange = () => {
    this.loadOsv();
  };

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService, private settingsStore: SettingsStore) {
    makeAutoObservable(this);

    this.loadOsv();
    i18next.on('languageChanged', this._handleLangChange);
  }

  dispose = () => {
    i18next.off('languageChanged', this._handleLangChange);
  };

  get sortedAbonents() {
    return this._abonents.slice().sort((a, b) => flatComparator(a.flat[0], b.flat[0]));
  }

  get abonentsCount() {
    return this._abonents.length;
  }

  // OSI-806: признак наличия у ОСИ дочерних помещений (паркинги/кладовки).
  // Источник правды — общий MobX-сервис OsiService (osiInfo.hasChildAbonents), критерий считает бэк.
  // Нужен UI, чтобы НЕ показывать чекбокс «Свернуть дочерние помещения» в домах, где сворачивать нечего.
  // ВАЖНО: сам флаг isCollapseChildren при скрытом чекбоксе НАМЕРЕННО НЕ сбрасываем (остаётся true) —
  // дочерних помещений нет, поэтому ни локальная свёртка (collapseOsvChildren), ни query-параметр
  // collapseChildren в выгрузке отчёта ничего не меняют.
  get hasChildAbonents(): boolean {
    return this.osiModule.osiInfo?.hasChildAbonents ?? false;
  }

  downloadOsv = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId || !this.osiModule.osiInfo?.name) throw new Error('osiId or osiName is undefined');

      this.isLoading = true;
      const db = moment.utc(this._startDate).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
      const de = moment.utc(this._endDate).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]');

      // Передаём флаг сворачивания дочерних помещений последним аргументом
      const data = await api.getOSVReport(this.osiModule.osiId, db, de, undefined, this.isCollapseChildren);

      const house = this.osiModule.osiInfo?.address
        ?.split(',')
        .slice(-1)
        .pop()
        ?.replace('д.', '')
        .replace('дом', '')
        .trim();
      const osiName = this.osiModule.osiInfo?.name.startsWith(this.osiModule.osiInfo?.unionTypeRu ?? '')
        ? this.osiModule.osiInfo?.name
        : `${this.osiModule.osiInfo?.unionTypeRu} ${this.osiModule.osiInfo?.name}`;
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
      downloadBase64(
        data as unknown as string,
        MIME.XLSX,
        `Оборотно_сальдовая_ведомость_${osiName}_дом_${house}_${db.substring(0, 10)}_${de.substring(0, 10)}.xlsx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  loadOsv = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;

      this._startDate = moment(this.dateFieldValue).startOf('month').toDate();
      this._endDate = moment(this.dateFieldValue).endOf('month').toDate();

      const queryOptions = getGetApiTransactionsOsvOsiIdQueryOptions(
        this.osiModule.osiId,
        {
          dateBegin: moment.utc(this._startDate).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          dateEnd: moment.utc(this._endDate).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        },
        { query: { gcTime: 0, staleTime: 0 } }
      );

      const response = await queryClient.fetchQuery(queryOptions);

      if (response.code !== 0) {
        throw new Error(response.message ?? 'Ошибка при запросе OSV');
      }

      const osvAbonents = response?.result?.abonents ?? [];

      // Сохраняем сырой список до возможного return — чтобы при пустом ответе грид очистился
      this._rawOsvAbonents = osvAbonents;

      if (!osvAbonents?.length) {
        // Пустой ответ — пересобираем строки (получим пустой грид), карта родителей не нужна
        this.rebuildRows();
        return;
      }

      // Догружаем абонентов ОСИ и строим карту child→parent.
      // Устойчиво: при ошибке логируем, но не роняем построение строк — карта остаётся пустой.
      try {
        const abonents = await getOsiAbonents(this.osiModule.osiId);
        this._childToParent = buildChildToParent(abonents);
      } catch (abonentsError) {
        logger.error(abonentsError);
        this._childToParent = new Map();
      }

      // Строки строим централизованно (учитывает флаг схлопывания)
      this.rebuildRows();
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message);
      }
    } finally {
      this.isLoading = false;
    }
  };

  // Пересборка строк грида из кэша _rawOsvAbonents с учётом флага схлопывания.
  // Без сетевого запроса: при включённом флаге дочерние помещения вливаются в родителя.
  private rebuildRows = () => {
    const src = this.isCollapseChildren
      ? collapseOsvChildren(this._rawOsvAbonents, this._childToParent)
      : this._rawOsvAbonents;

    const [rows, total] = getOsvRows(src, this.settingsStore.locale);
    this._abonents = rows.map((e: Abonent, id: number) => ({ id, ...e }));
    this.totalsOsv = total;

    // OSI-808: итоги считаем ЗДЕСЬ, а не в loadOsv, и по `rows` (не по _rawOsvAbonents):
    // в rebuildRows строки приходят уже после схлопывания дочерних помещений
    // (collapseOsvChildren) и с локалью, применённой getOsvRows, поэтому «ИТОГО» и разбивка
    // по категориям всегда совпадают с тем, что показано в таблице. Побочный эффект —
    // пересчёт происходит на ВСЕ сценарии пересборки: смена месяца, «Обновить», смена языка
    // (loadOsv по languageChanged) и чекбокс «Свернуть дочерние помещения» (setCollapseChildren).
    //
    // Подпись «ИТОГО» переводим через settingsStore.intl: это тот же react-intl, что стоит за
    // хуком useTranslation в виджете (OsiOsvWidget.tsx), но доступный из MobX-класса —
    // SettingsStore пересоздаёт intl в autorun при смене locale. defaultMessage берём из
    // русского словаря — ровно как translateToken в useTranslation.ts.
    const totalLabelId = tokens.osiOSV.table.footer.total;
    this.osvTotalRows = buildOsvTotalRows(
      getOsvCategoryTotals(rows, this.settingsStore.locale),
      this.settingsStore.intl.formatMessage({
        id: totalLabelId,
        defaultMessage: messages[LOCALES.RU][totalLabelId]
      })
    );
  };

  // Сеттер флага схлопывания — пересобирает строки локально, без повторного запроса
  setCollapseChildren = (value: boolean) => {
    this.isCollapseChildren = value;
    this.rebuildRows();
  };
}

export function getOsvRows(abonents: ReportsOSVAbonent[], langKey: LOCALE) {
  const translatedField = langKey === LOCALES.RU ? 'serviceName' : 'serviceNameKz';

  // Фаза 2: отображаемый номер помещения — арендатор → flatDisplay (единый номер от backend),
  // иначе makeFlat (с префиксом типа: Неж-/Парк-/Клад-). Слепая замена makeFlat→flatDisplay сломала бы
  // паркинги/кладовки/нежилые, т.к. backend не добавляет префиксы для не-арендаторов.
  const displayFlat = (item: ReportsOSVAbonent) =>
    item.areaTypeCode === AreaTypeCodes.ARENDATOR ? (item.flatDisplay ?? item.flat) : makeFlat(item);

  const rows = abonents.reduce((acc: any, item) => {
    const abonentData = {
      level: 0,
      abonentName: item.abonentName,
      owner: item.owner,
      flat: [displayFlat(item)],
      totalBegin: sumBy(item.services, 'begin'),
      totalDebet: sumBy(item.services, 'sumOfAccurals'),
      totalFixes: sumBy(item.services, 'sumOfFixes'),
      totalKredit: sumBy(item.services, 'kredit'),
      totalFine: sumBy(item.services, 'sumOfFines'),
      totalEnd: sumBy(item.services, 'end'),
      id: [item.abonentId]
    };

    const servicesData = item.services?.map((service) => ({
      ...service,
      level: 1,
      flat: [displayFlat(item), service?.[translatedField]],
      id: [abonentData.id, service?.[translatedField]],
      totalBegin: service.begin,
      totalDebet: service.sumOfAccurals,
      totalFixes: service.sumOfFixes,
      totalKredit: service.kredit,
      totalFine: service.sumOfFines,
      totalEnd: service.end
    }));

    const result = [...acc, abonentData, ...(servicesData ?? [])];

    return result;
  }, []);

  const total = [
    sumBy(rows, 'begin'),
    sumBy(rows, 'sumOfAccurals'),
    sumBy(rows, 'sumOfFixes'),
    sumBy(rows, 'kredit'),
    sumBy(rows, 'end'),
    sumBy(rows, 'sumOfFines')
  ];
  return [rows, total];
}
